import { downloadFile, type ReportExportTable } from "./reportExport";

export interface ExcelExportColumn<T> {
  header: string;
  value: (item: T) => string | number | Date | boolean | null | undefined;
  width?: number;
}

export async function exportToExcel<T>(
  items: T[],
  columns: ExcelExportColumn<T>[],
  fileName: string,
  sheetName = "Export",
): Promise<void> {
  // Loaded lazily to keep exceljs out of the main bundle
  const { Workbook } = await import("exceljs");
  const workbook = new Workbook();
  // Excel limits sheet names to 31 characters
  const worksheet = workbook.addWorksheet(sheetName.substring(0, 31));

  worksheet.columns = columns.map((col) => ({
    header: col.header,
    width: col.width ?? 20,
  }));

  items.forEach((item) => {
    worksheet.addRow(columns.map((col) => col.value(item) ?? ""));
  });

  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFE7E6E6" },
  };
  worksheet.views = [{ state: "frozen", ySplit: 1 }];
  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: columns.length },
  };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName.toLowerCase().endsWith(".xlsx")
    ? fileName
    : `${fileName}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// Writes the rows of a report table as shown on screen. Group headers become merged rows.
export async function exportReportToExcel(
  table: ReportExportTable,
  fileName: string,
): Promise<void> {
  const { Workbook } = await import("exceljs");
  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet(table.title.substring(0, 31) || "Export");
  const columnCount = table.headers.length;

  worksheet.columns = table.headers.map((header) => ({ header, width: 20 }));

  table.rows.forEach((row) => {
    if ("group" in row) {
      const added = worksheet.addRow([row.group]);
      worksheet.mergeCells(added.number, 1, added.number, columnCount);
      added.font = { bold: true };
      added.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF3F2F1" } };
      return;
    }
    const added = worksheet.addRow(row.cells.map((cell) => cell.amount ?? cell.text));
    row.cells.forEach((cell, index) => {
      const target = added.getCell(index + 1);
      if (cell.amount !== undefined) {
        target.numFmt = cell.currency ? '#,##0.00 "€"' : "#,##0.00";
      }
      if (cell.alignRight) {
        target.alignment = { horizontal: "right" };
      }
    });
  });

  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE7E6E6" } };
  worksheet.views = [{ state: "frozen", ySplit: 1 }];
  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: columnCount },
  };

  const buffer = await workbook.xlsx.writeBuffer();
  downloadFile(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    fileName.toLowerCase().endsWith(".xlsx") ? fileName : `${fileName}.xlsx`,
  );
}
